import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Copy, Check } from './icons';
import type { ExportColorEntry, ExportFormat } from '../utils/codeExport';
import { copyResult } from '../utils/analytics';
import { FORMATTERS, getExportRoleChecks, getExportSourceUrl } from '../utils/codeExport';

interface Props {
  colors: ExportColorEntry[];
  contrastRatio?: number;
  isOpen: boolean;
  onClose: () => void;
}

const TAB_GROUPS: { group: string; tabs: { id: ExportFormat; label: string }[] }[] = [
  {
    group: 'Core',
    tabs: [
      { id: 'css',          label: 'CSS' },
      { id: 'scss',         label: 'SCSS' },
      { id: 'json',         label: 'JSON' },
    ],
  },
  {
    group: 'Tailwind',
    tabs: [
      { id: 'tailwind-v3',  label: 'TW v3' },
      { id: 'tailwind-v4',  label: 'TW v4' },
    ],
  },
  {
    group: 'Frameworks',
    tabs: [
      { id: 'shadcn',       label: 'shadcn HSL' },
      { id: 'daisyui',      label: 'DaisyUI v3' },
      { id: 'bootstrap',    label: 'Bootstrap' },
      { id: 'material-ui',  label: 'MUI' },
      { id: 'chakra',       label: 'Chakra' },
    ],
  },
  {
    group: 'Tokens',
    tabs: [
      { id: 'dtcg',              label: 'DTCG 2025.10' },
      { id: 'style-dictionary',  label: 'Style Dict' },
      { id: 'bookmarklet',       label: 'Bookmarklet' },
    ],
  },
];

const ALL_TABS = TAB_GROUPS.flatMap(g => g.tabs);

const CodeExportModal: React.FC<Props> = ({ colors, contrastRatio, isOpen, onClose }) => {
  const { pathname, search } = useLocation();
  const [activeFormat, setActiveFormat] = useState<ExportFormat>('css');
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState('');
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  // A user may export before the page's debounced address update, or from /studio itself.
  const sourcePath = /^\/studio(?:\/|$)/.test(pathname)
    ? '/studio/' + colors.map(color => color.hex.replace('#', '').toUpperCase()).join('-')
    : pathname.startsWith('/color/') && colors[0] ? '/color/' + colors[0].hex.replace('#', '').toUpperCase() : pathname;
  const sourceUrl = getExportSourceUrl(sourcePath + search);
  const code = FORMATTERS[activeFormat](colors, contrastRatio, { sourceUrl });
  const roleChecks = getExportRoleChecks(activeFormat, colors);

  const handleCopy = useCallback(async () => {
    const ok = await copyResult(code, 'palette', 'export', activeFormat);
    setCopyError(ok ? '' : 'Clipboard unavailable. Select and copy the displayed code.');
    setCopied(ok);
    setTimeout(() => setCopied(false), 2000);
  }, [code, activeFormat]);

  useEffect(() => {
    if (!isOpen) return;
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); closeRef.current(); }
      if (e.key !== 'Tab') return;
      const controls = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>('a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex="0"]') ?? [])
        .filter(element => element.getClientRects().length > 0);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first) { e.preventDefault(); dialogRef.current?.focus(); return; }
      if (e.shiftKey && (document.activeElement === first || !dialogRef.current?.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && (document.activeElement === last || !dialogRef.current?.contains(document.activeElement))) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4"
      style={{ background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Export color code"
        tabIndex={-1}
        className="w-full max-w-2xl max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain rounded-card bg-white shadow-floating"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 sm:px-6 py-4 border-b border-surface-border">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-ink">Export Code</h2>
            <p className="text-sm text-ink-secondary mt-0.5">
              Copy your colors as developer-ready code
            </p>
          </div>
          <button
            onClick={onClose}
            className="ui-icon-button"
            aria-label="Close export dialog"
          >
            <X size={18} />
          </button>
        </div>

        {/* Color preview chips */}
        <div className="flex flex-wrap gap-2 px-4 sm:px-6 pt-4">
          {colors.map(c => (
            <div key={c.name} className="flex max-w-full flex-wrap items-center gap-2 bg-surface rounded-control px-3 py-1.5 border border-surface-border">
              <span
                className="w-4 h-4 rounded-full border border-white shadow-sm flex-shrink-0"
                style={{ background: c.hex }}
              />
              <span className="text-xs font-mono text-ink-secondary">{c.hex}</span>
              <span className="text-xs text-ink-muted break-all">{c.name}</span>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="px-4 sm:px-6 pt-4 space-y-2">
          {TAB_GROUPS.map(group => (
            <div key={group.group} className="flex items-center gap-1">
              <span className="text-xs font-medium text-ink-muted uppercase tracking-wider w-20 shrink-0">{group.group}</span>
              <div className="flex gap-1 flex-wrap">
                {group.tabs.map(tab => (
                  <button
                    key={tab.id}
                    aria-pressed={activeFormat === tab.id}
                    onClick={() => setActiveFormat(tab.id)}
                    className={`min-h-11 px-3 py-2 rounded-control text-sm font-medium transition-colors ${
                      activeFormat === tab.id
                        ? 'bg-brand-600 text-white'
                        : 'text-ink-secondary hover:text-brand-600 hover:bg-brand-50'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Code block */}
        <div className="mx-4 sm:mx-6 mt-3 rounded-xl overflow-hidden border border-zinc-700">
          {activeFormat === 'bookmarklet' ? (
            <div className="bg-zinc-900 p-4 space-y-3">
              <p className="text-xs text-zinc-400">
                Drag the button below to your bookmarks bar, or copy the code.
                Clicking it on any page injects your palette as CSS variables.
              </p>
              <a
                href={code}
                onClick={e => e.preventDefault()}
                draggable
                className="inline-block px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 cursor-grab active:cursor-grabbing"
              >
                Inject Palette
              </a>
              <pre className="text-green-300 text-xs font-mono overflow-auto max-h-40 leading-relaxed mt-2">
                <code>{code}</code>
              </pre>
            </div>
          ) : (
            <pre className="bg-zinc-900 text-green-300 text-xs font-mono p-4 overflow-auto max-h-72 leading-relaxed">
              <code>{code}</code>
            </pre>
          )}
        </div>

        {roleChecks.length > 0 && <section aria-label="Exported text contrast checks" className="mx-4 sm:mx-6 mt-4 ui-panel p-4">
          <h3 className="font-semibold text-sm">Exported text pairs</h3>
          <p className="text-xs text-ink-secondary mt-1">Normal text AA, 4.5:1. These checks use the actual emitted HSL values. Recheck component overrides, boundaries and focus indicators in your application.</p>
          <ul className="space-y-1 mt-3 text-xs">
            {roleChecks.map(check => <li key={check.foregroundToken + '/' + check.backgroundToken} className="flex flex-wrap items-center justify-between gap-2">
              <span className="font-mono break-all">{check.foregroundToken} on {check.backgroundToken}</span>
              <span className={check.passes ? 'text-emerald-800' : 'text-red-700'}>{check.ratio.toFixed(3)}:1 · {check.passes ? 'Pass' : 'Fail'}</span>
            </li>)}
          </ul>
          {roleChecks.some(check => !check.passes) && <p className="text-sm text-red-700 mt-3">A generated state fails normal text AA. Adjust its background or override the content color for that state before using it.</p>}
        </section>}

        <p role="status" className="px-4 sm:px-6 text-sm text-red-700">{copyError}</p>
        {sourceUrl && <p className="px-4 sm:px-6 mt-3 text-xs text-ink-secondary">Reopen these colors: <a href={sourceUrl} className="text-brand-600 underline break-all">{sourceUrl}</a></p>}
        {/* Footer */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4">
          <span className="text-xs text-ink-muted">
            {ALL_TABS.find(t => t.id === activeFormat)?.label} format
            {contrastRatio ? ` · Input pair only: ${contrastRatio.toFixed(3)}:1` : ''}
          </span>
          <button
            onClick={handleCopy}
            className={`ui-button ${
              copied
                ? 'bg-emerald-700 text-white'
                : 'bg-brand-600 text-white hover:bg-brand-700'
            }`}
          >
            {copied ? <Check size={15} /> : <Copy size={15} />}
            {copied ? 'Copied!' : 'Copy Code'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CodeExportModal;
